import { ProfileProps } from '@common/types';
import Header from '@components/Header';
import ContactStatusToast from '@components/Profile/ContactStatusToast';
import ProfileTemplates from '@components/Profile/ProfileTemplates/ProfileTemplates';
import SectionWrapper from '@components/Profile/SectionWrapper';
import {
  PAGE_NAMES,
  PROFILE_PAGE_DATA_HOOKS,
} from '@utils/partnerProfileUtils';
import { Box, Divider } from '@wix/design-system';
import { useIntersect } from '@wix/experts-common';
import clsx from 'clsx';
import CopyNotification from '@components/Profile/CopyNotification';
import { FC, useState } from 'react';
import {
  useProfileContext,
  ACTIONS,
} from '@components/Contexts/ProfileContext';
import About from './About';
import BackToExplorer from './BackToExplorer';
import ClientProjects from './ClientProjects';
import ContactPanel from './ContactPanel';
import MiniContactPanel from './MiniContactPanel';
import NavBar from './NavBar';
import StartingRates from './StartingRates';
import ProjectsReviews from './ProjectsReviews';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';
import { useProfileSections } from './hooks/useProfileSections';
import s from './Profile.module.scss';

const ProfileMobile: FC<ProfileProps> = ({
  onContactClicked,
  onCopyClicked,
  headerOnClick,
  showBack,
  certifications,
  scrollToSection,
  sessionId,
  isRenderedInModal,
  numberOfTemplates,
  navigationRefs,
  copy,
  profileUrl,
  contactStatus,
  clearStatusMap,
}) => {
  const { copyStatus, dispatch } = useProfileContext();
  const {
    shouldShowClientProjects,
    shouldShowServices,
    shouldShowReviews,
    shouldShowTemplates,
    shouldShowServicesAndReviews,
    isLastSection,
  } = useProfileSections(numberOfTemplates);

  const [ref] = useIntersect({}) as any;
  const [isMiniContactPanelVisible, setIsMiniContactPanelVisible] =
    useState(false);

  const setCopyStatus = (status: null | 'success' | 'error') => {
    dispatch({ type: ACTIONS.SET_COPY_STATUS, copyStatus: status });
  };

  return (
    <Box
      display="block"
      dataHook={PROFILE_PAGE_DATA_HOOKS.MOBILE_PROFILE_PAGE}
      className={`${s.mobileContainer} ${s.profileAccessibility}`}
    >
      {!isRenderedInModal ? (
        <div className={`${s.stickyHeader} ${s.mobile}`}>
          <Header
            onClick={headerOnClick}
            pageName={PAGE_NAMES.PROFILE_PAGE}
            sessionId={sessionId}
          />
          <Divider skin="light" />
        </div>
      ) : null}
      <ContactStatusToast
        status={contactStatus}
        clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
        isMobileView={true}
      />
      <CopyNotification
        status={copyStatus}
        clearStatus={() => setCopyStatus(null)}
        isMobileView={true}
      />
      <BackToExplorer showBack={showBack} isMobileView={true} />
      <Box paddingTop={showBack ? '0px' : '30px'}>
        <ContactPanel
          isMobileView={true}
          onContactClicked={onContactClicked}
          copy={copy}
          onCopyClicked={onCopyClicked}
          shareUrl={profileUrl}
          certifications={certifications}
        />
      </Box>
      <div ref={ref} className={clsx(s.stickyMiniContactPanel)}>
        <MiniContactPanel
          onContactClicked={onContactClicked}
          copy={copy}
          onCopyClicked={onCopyClicked}
          shareUrl={profileUrl}
          onVisibilityChange={setIsMiniContactPanelVisible}
        />
      </div>
      <NavBar
        isMobileView={true}
        navigationRefs={navigationRefs}
        isMiniContactPanelVisible={isMiniContactPanelVisible}
      />
      <Box
        dataHook="profile-page-sections"
        className={`${s.profilePageSections} ${s.mobile}`}
        direction="vertical"
      >
        <SectionWrapper
          sectionId={PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
          ref={navigationRefs}
          visible={shouldShowClientProjects}
          showDivider={!isLastSection.clientProjects}
        >
          <ClientProjects
            isMobileView={true}
            onShowLess={() =>
              scrollToSection(
                PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
                isMiniContactPanelVisible,
              )
            }
          />
        </SectionWrapper>

        <SectionWrapper
          sectionId={PROFILE_PAGE_SECTIONS.ABOUT}
          ref={navigationRefs}
          showDivider={!isLastSection.about}
        >
          <About
            isMobileView={true}
            certifications={certifications}
            onShowLess={() =>
              scrollToSection(
                PROFILE_PAGE_SECTIONS.ABOUT,
                isMiniContactPanelVisible,
              )
            }
          />
        </SectionWrapper>

        <SectionWrapper
          sectionId={PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS}
          ref={navigationRefs}
          visible={shouldShowServicesAndReviews}
          className={s.servicesAndReviewsSection}
        >
          <Box direction="vertical">
            <SectionWrapper
              sectionId={PROFILE_PAGE_SECTIONS.SERVICES}
              ref={navigationRefs}
              visible={shouldShowServices}
              showDivider={!isLastSection.services}
            >
              <StartingRates isMobileView={true} />
            </SectionWrapper>
            <SectionWrapper
              sectionId={PROFILE_PAGE_SECTIONS.REVIEWS}
              ref={navigationRefs}
              visible={shouldShowReviews}
              showDivider={!isLastSection.reviews}
            >
              <ProjectsReviews
                isMobileView={true}
                onPageChange={() =>
                  scrollToSection(
                    PROFILE_PAGE_SECTIONS.REVIEWS,
                    isMiniContactPanelVisible,
                  )
                }
              />
            </SectionWrapper>
          </Box>
        </SectionWrapper>
        <SectionWrapper
          sectionId={PROFILE_PAGE_SECTIONS.TEMPLATES}
          ref={navigationRefs}
          visible={shouldShowTemplates}
          showDivider={!isLastSection.templates}
        >
          <ProfileTemplates
            isMobileView={true}
            onShowLess={() =>
              scrollToSection(
                PROFILE_PAGE_SECTIONS.TEMPLATES,
                isMiniContactPanelVisible,
              )
            }
          />
        </SectionWrapper>
      </Box>
    </Box>
  );
};

export default ProfileMobile;
